import { useState } from "react";
import { Button, SectionLabel } from "../ui.js";

/**
 * The toss.
 *
 * A real toss has four steps, and skipping any of them is how arguments start
 * at a ground: someone calls, the coin goes up, the call is right or wrong, and
 * only then does the winner choose. The old form just asked "who won" and
 * trusted the answer.
 *
 * The flip is decided before the animation begins — the animation shows a
 * result, it does not produce one — so a slow phone cannot change the outcome.
 */

export type Side = "HEADS" | "TAILS";
export interface TossResult {
  winnerTeamId: string;
  decision: "BAT" | "BOWL";
  callingTeamId: string;
  called: Side;
  landed: Side;
}

type Step = "who" | "call" | "flipping" | "result" | "decide";

export function Toss({ home, away, onDone, onCancel }: {
  home: { id: string; name: string; short: string };
  away: { id: string; name: string; short: string };
  onDone: (r: TossResult) => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<Step>("who");
  const [caller, setCaller] = useState<string>("");
  const [called, setCalled] = useState<Side>("HEADS");
  const [landed, setLanded] = useState<Side>("HEADS");

  const teamOf = (id: string) => (id === home.id ? home : away);
  const other = (id: string) => (id === home.id ? away : home);
  const winnerId = called === landed ? caller : other(caller).id;

  const flip = (side: Side) => {
    setCalled(side);
    // Decide first, animate second.
    const result: Side = crypto.getRandomValues(new Uint8Array(1))[0]! < 128 ? "HEADS" : "TAILS";
    setLanded(result);
    setStep("flipping");
    setTimeout(() => setStep("result"), 2100);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 split:items-center split:p-6"
         onClick={step === "flipping" ? undefined : onCancel}>
      <div className="w-full max-w-[460px] rounded-t-2xl border border-line bg-panel
                      px-5 pb-[calc(20px+var(--sab))] pt-5 split:rounded-2xl"
           onClick={(e) => e.stopPropagation()}>

        <h2 className="mb-1 font-num text-head font-semibold">The toss</h2>
        <p className="mb-5 text-body text-faint">{home.name} v {away.name}</p>

        {step === "who" && (
          <>
            <SectionLabel className="mb-1.5 block">Who calls?</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {[home, away].map((t) => (
                <button key={t.id} onClick={() => { setCaller(t.id); setStep("call"); }}
                  className="rounded-card border border-line bg-raise px-3 py-4 text-left">
                  <span className="block font-num text-title font-semibold text-amber">{t.short}</span>
                  <span className="mt-0.5 block text-caption text-faint">{t.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "call" && (
          <>
            <SectionLabel className="mb-1.5 block">{teamOf(caller).short} calls it</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {(["HEADS", "TAILS"] as const).map((s) => (
                <button key={s} onClick={() => flip(s)}
                  className="rounded-card border border-line bg-raise px-3 py-5 font-num
                             text-sub font-semibold">
                  {s === "HEADS" ? "Heads" : "Tails"}
                </button>
              ))}
            </div>
            <button onClick={() => setStep("who")}
              className="mt-3 w-full py-2 text-center text-body text-faint">Back</button>
          </>
        )}

        {step === "flipping" && (
          <div className="py-6 text-center">
            <Coin spinning landed={landed} />
            <p className="mt-5 font-mono text-caption text-faint">
              {teamOf(caller).short} called {called.toLowerCase()}…
            </p>
          </div>
        )}

        {step === "result" && (
          <div className="py-4 text-center">
            <Coin landed={landed} />
            <p className="mt-4 font-mono text-caption uppercase tracking-[.14em] text-faint">
              It is {landed.toLowerCase()}
            </p>
            <p className="mt-2 font-num text-head font-semibold text-amber">
              {teamOf(winnerId).name} win the toss
            </p>
            <p className="mt-1 text-body text-faint">
              {teamOf(caller).short} called {called.toLowerCase()} — {called === landed ? "correct" : "wrong"}
            </p>
            <div className="mt-5">
              <Button full onClick={() => setStep("decide")}>
                {teamOf(winnerId).short} choose
              </Button>
            </div>
          </div>
        )}

        {step === "decide" && (
          <>
            <SectionLabel className="mb-1.5 block">{teamOf(winnerId).name} elect to</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {(["BAT", "BOWL"] as const).map((d) => (
                <button key={d}
                  onClick={() => onDone({ winnerTeamId: winnerId, decision: d,
                                          callingTeamId: caller, called, landed })}
                  className="rounded-card border border-line bg-raise px-3 py-5 font-num
                             text-sub font-semibold hover:border-amber hover:text-amber">
                  {d === "BAT" ? "Bat first" : "Bowl first"}
                </button>
              ))}
            </div>
            <p className="mt-3 text-center text-caption text-faint">
              {other(winnerId).short} {" "}will do the other.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The coin.
 *
 * Two faces on a 3D-rotating card. The spin count is odd or even depending on
 * which face must show, so it always ends facing the right way — the animation
 * is honest about a result already decided.
 */
function Coin({ spinning, landed }: { spinning?: boolean; landed: Side }) {
  const turns = landed === "HEADS" ? 5 : 5.5;   // half-turns land on the far face
  return (
    <>
      <style>{`
        @keyframes orbit-toss {
          0%   { transform: rotateX(0deg)   translateY(0)     scale(1); }
          35%  { transform: rotateX(900deg) translateY(-46px) scale(1.08); }
          100% { transform: rotateX(${turns * 360}deg) translateY(0) scale(1); }
        }
        .coin-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
      `}</style>
      <div className="mx-auto" style={{ perspective: "600px", width: 96, height: 96 }}>
        <div style={{
          width: 96, height: 96, position: "relative", transformStyle: "preserve-3d",
          animation: spinning ? "orbit-toss 2s cubic-bezier(.24,.86,.3,1) forwards" : "none",
          transform: spinning ? undefined : `rotateX(${landed === "HEADS" ? 0 : 180}deg)`,
        }}>
          <Face label="H" tone="amber" />
          <Face label="T" tone="chalk" back />
        </div>
      </div>
    </>
  );
}

function Face({ label, tone, back }: { label: string; tone: "amber" | "chalk"; back?: boolean }) {
  return (
    <div className="coin-face absolute inset-0 flex items-center justify-center rounded-full border-[3px]"
      style={{
        transform: back ? "rotateX(180deg)" : undefined,
        borderColor: tone === "amber" ? "#E9A13B" : "#F2EFE6",
        background: tone === "amber" ? "#20180C" : "#14181C",
        color: tone === "amber" ? "#E9A13B" : "#F2EFE6",
        fontFamily: "'Barlow Condensed', sans-serif",
        fontSize: 40, fontWeight: 600,
      }}>
      {label}
    </div>
  );
}
